import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, formatBytes, inboxApi, renderTemplate, templateBlockedReason, templateButtons,
  type Template, type TemplateSendExtras,
} from '@wa/shared';

interface Props {
  open: boolean;
  /** Where a header file is uploaded to — the same asset an ordinary media send uses. */
  conversationId: number;
  onClose: () => void;
  onSend: (templateId: number, variables: string[], extras: TemplateSendExtras) => void;
  isSending: boolean;
  /** A rejected send, shown here rather than in the composer the dialog covers. */
  sendError: string | null;
}

/**
 * Picking an approved template and filling its values.
 *
 * This is the only way to reopen a conversation past the 24-hour window, so it
 * is reachable from the closed composer in one tap rather than from a menu.
 *
 * Two panes on a wide screen, one at a time on a narrow one — the same tree in
 * both cases, so there is no second copy to keep in step. The full-screen
 * treatment on a phone is deliberate: filling three values in a strip above the
 * keyboard is how the wrong value gets sent.
 */
export function TemplatePicker({ open, conversationId, onClose, onSend, isSending, sendError }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Template | null>(null);
  const [values, setValues] = useState<string[]>([]);
  const [headerText, setHeaderText] = useState('');
  const [headerFile, setHeaderFile] = useState<{ assetId: number; name: string; size: number; previewUrl: string | null; mime: string } | null>(null);

  // The chosen picture, shown in the preview from the browser's own copy. Freed
  // when it is replaced or the picker closes.
  useEffect(() => () => { if (headerFile?.previewUrl) URL.revokeObjectURL(headerFile.previewUrl); }, [headerFile]);
  const [buttonValues, setButtonValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const searchBox = useRef<HTMLInputElement>(null);
  const filePicker = useRef<HTMLInputElement>(null);

  // Goes to Meta first, like every send, and comes back as an asset id the
  // template send names — the same row an ordinary attachment uses.
  const uploadHeader = useMutation({
    mutationFn: (file: File) => inboxApi.upload(conversationId, file),
    onSuccess: (uploaded, file) => {
      setError(null);
      setHeaderFile({
        assetId: uploaded.mediaAssetId, name: file.name, size: file.size, mime: file.type,
        previewUrl: file.type.startsWith('image/') || file.type.startsWith('video/') ? URL.createObjectURL(file) : null,
      });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not upload the header file.'),
  });

  // Templates change when someone edits them at Meta, which is rare and never
  // mid-conversation. Refetching on every open would cost a request per send.
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: inboxApi.templates,
    staleTime: 300_000,
    enabled: open,
  });

  const sync = useMutation({
    mutationFn: inboxApi.syncTemplates,
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not reach Meta.'),
  });

  // Escape closes; the page behind must not scroll under the dialog.
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  // Reopening starts clean: last time's half-filled values belonged to another
  // customer, and sending them would be worse than retyping.
  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setValues([]);
    setError(null);
    setSearch('');
    searchBox.current?.focus();
  }, [open]);

  const items = useMemo(() => {
    const all = templates.data ?? [];
    const needle = search.trim().toLowerCase();
    const matched = needle
      ? all.filter((t) =>
          t.name.toLowerCase().includes(needle) ||
          (t.bodyText ?? '').toLowerCase().includes(needle))
      : all;

    // Sendable first. A blocked template is still listed, because an agent who
    // cannot find the one they wanted assumes the screen is broken.
    return [...matched].sort((a, b) => {
      const blocked = Number(templateBlockedReason(a) !== null) - Number(templateBlockedReason(b) !== null);
      return blocked !== 0 ? blocked : a.name.localeCompare(b.name);
    });
  }, [templates.data, search]);

  if (!open) return null;

  const blocked = selected ? templateBlockedReason(selected) : null;
  const paramButtons = selected ? templateButtons(selected).filter((b) => b.param && typeof b.index === 'number') : [];
  const missing = selected
    ? Array.from({ length: selected.bodyVariables }, (_, i) => values[i]?.trim() ?? '').some((v) => v === '')
      || (Boolean(selected.needsHeaderText) && headerText.trim() === '')
      || (selected.needsHeaderMedia && headerFile === null)
      || paramButtons.some((b) => (buttonValues[String(b.index)] ?? '').trim() === '')
    : true;

  function choose(template: Template) {
    setSelected(template);
    setValues(Array.from({ length: template.bodyVariables }, () => ''));
    setHeaderText('');
    setHeaderFile(null);
    setButtonValues({});
    setError(null);
  }

  function submit() {
    if (!selected || blocked || missing || isSending || uploadHeader.isPending) return;

    const extras: TemplateSendExtras = {};
    if (selected.needsHeaderText) extras.headerText = headerText.trim();
    if (selected.needsHeaderMedia && headerFile) extras.headerMediaAssetId = headerFile.assetId;
    if (paramButtons.length > 0) {
      extras.buttonParams = Object.fromEntries(paramButtons.map((b) => [String(b.index), (buttonValues[String(b.index)] ?? '').trim()]));
    }

    onSend(selected.id, values.slice(0, selected.bodyVariables).map((v) => v.trim()), extras);
  }

  /** The header format Meta accepts for this template, as an input accept list. */
  const headerAccept = selected?.headerFormat === 'IMAGE' ? 'image/jpeg,image/png'
    : selected?.headerFormat === 'VIDEO' ? 'video/mp4,video/3gpp'
    : selected?.headerFormat === 'DOCUMENT' ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt'
    : '*/*';

  const list = (
    <div className="flex h-full min-h-0 flex-col border-(--color-line) sm:w-[19rem] sm:shrink-0 sm:border-r">
      <div className="shrink-0 border-b border-(--color-line) p-3">
        <input
          ref={searchBox}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search templates"
          aria-label="Search templates"
          className="tap w-full min-w-0 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2 text-body outline-none focus:border-(--color-brand)"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {templates.isPending && (
          <p className="p-4 text-body text-(--color-muted)">Loading templates…</p>
        )}

        {templates.isError && (
          <p className="p-4 text-body text-(--color-bad)">Could not load templates.</p>
        )}

        {templates.isSuccess && items.length === 0 && (
          <div className="p-4">
            <p className="text-body text-(--color-muted)">
              {search ? 'No template matches that.' : 'No templates yet.'}
            </p>
            {!search && (
              <p className="mt-2 text-small text-(--color-muted)">
                Create them in WhatsApp Manager, then pull them in with Refresh.
              </p>
            )}
          </div>
        )}

        <ul>
          {items.map((template) => {
            const why = templateBlockedReason(template);
            const active = selected?.id === template.id;

            return (
              <li key={template.id}>
                <button
                  type="button"
                  onClick={() => choose(template)}
                  aria-current={active}
                  className={`tap w-full border-b border-(--color-line) px-3 py-2.5 text-left ${
                    active ? 'bg-(--color-sunken)' : 'hover:bg-(--color-sunken)'
                  }`}
                >
                  <span className="flex items-baseline gap-2">
                    <span className={`min-w-0 flex-1 truncate text-small font-medium ${why ? 'text-(--color-muted)' : ''}`}>
                      {template.name}
                    </span>
                    <span className="shrink-0 text-micro uppercase text-(--color-muted)">
                      {template.language}
                    </span>
                  </span>

                  <span className="mt-0.5 block truncate text-micro text-(--color-muted)">
                    {why ?? template.bodyText ?? '—'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="shrink-0 border-t border-(--color-line) p-2">
        <button
          type="button"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="tap w-full rounded-lg border border-(--color-line) px-3 py-2 text-small text-(--color-muted) hover:text-(--color-ink) disabled:opacity-50"
        >
          {sync.isPending ? 'Refreshing from Meta…' : 'Refresh from Meta'}
        </button>
      </div>
    </div>
  );

  const detail = (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      {!selected ? (
        <div className="grid h-full place-items-center px-8 text-center">
          <p className="max-w-xs text-body text-(--color-muted)">
            Choose a template to preview it.
          </p>
        </div>
      ) : (
        <>
          <div className="chat-ground min-h-0 flex-1 overflow-y-auto p-4">
            {/* The same card the thread draws once it is sent — picture on top,
                header line, body, footer, then the buttons as rows of the same
                card — so what the agent approves here is what the customer sees. */}
            <div className="ml-auto w-fit min-w-56 max-w-[min(28rem,90%)] overflow-hidden rounded-lg rounded-br-sm bg-(--color-bubble-out) text-body leading-snug shadow-sm">
              {selected.needsHeaderMedia && (
                headerFile?.previewUrl && headerFile.mime.startsWith('image/') ? (
                  <img src={headerFile.previewUrl} alt="" className="block max-h-64 w-full object-cover" />
                ) : headerFile?.previewUrl && headerFile.mime.startsWith('video/') ? (
                  <video src={headerFile.previewUrl} className="block max-h-64 w-full" muted playsInline />
                ) : headerFile ? (
                  <div className="flex items-center gap-2 bg-black/5 px-3 py-2.5">
                    <span className="grid size-9 shrink-0 place-items-center rounded bg-(--color-surface) text-micro font-semibold text-(--color-muted)" aria-hidden="true">
                      {(headerFile.name.split('.').pop() ?? 'FILE').toUpperCase().slice(0, 4)}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-small font-medium">{headerFile.name}</span>
                      <span className="block text-micro text-(--color-muted)">{formatBytes(headerFile.size)}</span>
                    </span>
                  </div>
                ) : (
                  <div className="grid h-28 place-items-center bg-black/5 text-micro text-(--color-muted)">
                    {selected.headerFormat === 'IMAGE' ? 'Image' : selected.headerFormat === 'VIDEO' ? 'Video' : 'Document'} header — attach below
                  </div>
                )
              )}

              <div className="px-2.5 py-1.5">
                {selected.headerText && (
                  <p className="mb-1 font-semibold break-words">
                    {selected.needsHeaderText ? renderTemplate(selected.headerText, [headerText]) : selected.headerText}
                  </p>
                )}

                <p className="break-words whitespace-pre-wrap">
                  {renderTemplate(selected.bodyText, values)}
                </p>

                {selected.footerText && (
                  <p className="mt-1 text-micro text-(--color-muted) break-words">{selected.footerText}</p>
                )}
              </div>

              {templateButtons(selected).length > 0 && (
                <ul className="border-t border-black/10">
                  {templateButtons(selected).map((button, index) => (
                    <li
                      key={`${button.type}-${index}`}
                      className="flex items-center justify-center gap-1.5 border-b border-black/10 px-2.5 py-1.5 text-center text-small font-medium text-(--color-brand-strong) last:border-b-0"
                    >
                      <span aria-hidden="true" className="text-micro">
                        {button.type === 'URL' ? '↗' : button.type === 'PHONE_NUMBER' ? '📞' : button.type === 'COPY_CODE' || button.type === 'OTP' ? '⧉' : '↩'}
                      </span>
                      {button.text}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="shrink-0 space-y-2 border-t border-(--color-line) p-3">
            {blocked && (
              <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
                {selected.name} {blocked} and cannot be sent from here.
                {selected.rejectedReason && ` Meta said: ${selected.rejectedReason}.`}
              </p>
            )}

            {!blocked && selected.needsHeaderMedia && (
              <div className="flex items-center gap-2 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2">
                <input
                  ref={filePicker}
                  type="file"
                  className="hidden"
                  accept={headerAccept}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (!file) return;
                    if (file.size > 16 * 1024 * 1024) { setError(`${file.name} is ${formatBytes(file.size)}. WhatsApp accepts up to 16 MB.`); return; }
                    uploadHeader.mutate(file);
                  }}
                />
                <span className="min-w-0 flex-1">
                  {headerFile ? (
                    <>
                      <span className="block truncate text-small font-medium">{headerFile.name}</span>
                      <span className="block text-micro text-(--color-muted)">{formatBytes(headerFile.size)} — sent as the header</span>
                    </>
                  ) : (
                    <span className="block text-small text-(--color-muted)">
                      This template's header is {selected.headerFormat === 'IMAGE' ? 'an image' : selected.headerFormat === 'VIDEO' ? 'a video' : 'a document'}; attach the one to send.
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => filePicker.current?.click()}
                  disabled={uploadHeader.isPending || isSending}
                  className="tap shrink-0 rounded-lg border border-(--color-line) px-3 py-1.5 text-small font-medium hover:bg-(--color-surface) disabled:opacity-50"
                >
                  {uploadHeader.isPending ? 'Uploading…' : headerFile ? 'Replace' : 'Attach'}
                </button>
              </div>
            )}

            {!blocked && selected.needsHeaderText && (
              <label className="block min-w-0">
                <span className="mb-1 block text-micro text-(--color-muted)">Header {'{{1}}'}</span>
                <input
                  value={headerText}
                  maxLength={60}
                  onChange={(event) => setHeaderText(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') submit(); }}
                  className="tap w-full min-w-0 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2 text-body outline-none focus:border-(--color-brand)"
                />
              </label>
            )}

            {!blocked && paramButtons.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2">
                {paramButtons.map((button) => (
                  <label key={button.index} className="block min-w-0">
                    <span className="mb-1 block text-micro text-(--color-muted)">
                      {button.type === 'COPY_CODE' || button.type === 'OTP' ? `Code for "${button.text}"` : `Link ending for "${button.text}"`}
                    </span>
                    <input
                      value={buttonValues[String(button.index)] ?? ''}
                      onChange={(event) => setButtonValues((current) => ({ ...current, [String(button.index)]: event.target.value }))}
                      onKeyDown={(event) => { if (event.key === 'Enter') submit(); }}
                      className="tap w-full min-w-0 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2 text-body outline-none focus:border-(--color-brand)"
                    />
                  </label>
                ))}
              </div>
            )}

            {!blocked && selected.bodyVariables > 0 && (
              <div className="grid gap-2 sm:grid-cols-2">
                {Array.from({ length: selected.bodyVariables }, (_, index) => (
                  <label key={index} className="block min-w-0">
                    <span className="mb-1 block text-micro text-(--color-muted)">{`{{${index + 1}}}`}</span>
                    <input
                      value={values[index] ?? ''}
                      onChange={(event) =>
                        setValues((current) => {
                          const next = [...current];
                          next[index] = event.target.value;
                          return next;
                        })
                      }
                      onKeyDown={(event) => { if (event.key === 'Enter') submit(); }}
                      className="tap w-full min-w-0 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2 text-body outline-none focus:border-(--color-brand)"
                    />
                  </label>
                ))}
              </div>
            )}

            {(error ?? sendError) && (
              <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
                {error ?? sendError}
              </p>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={Boolean(blocked) || missing || isSending || uploadHeader.isPending}
              className="tap w-full rounded-lg bg-(--color-brand) px-4 py-2.5 text-body font-medium text-(--color-brand-ink) disabled:opacity-40"
            >
              {isSending ? 'Sending…' : missing && !blocked ? (selected.needsHeaderMedia && !headerFile ? 'Attach the header file to send' : 'Fill every value to send') : 'Send template'}
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Send a template">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/40" />

      <div className="absolute inset-0 flex flex-col bg-(--color-surface) shadow-xl sm:inset-auto sm:top-1/2 sm:left-1/2 sm:h-[min(34rem,88vh)] sm:w-[min(48rem,92vw)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border sm:border-(--color-line)">
        <header className="flex shrink-0 items-center gap-2 border-b border-(--color-line) px-3 py-2.5">
          {/* On one pane the back arrow returns to the list; on two it is redundant,
              because the list never left the screen. */}
          {selected && (
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-label="Back to the template list"
              className="tap grid shrink-0 place-items-center rounded px-1 text-(--color-muted) hover:text-(--color-ink) sm:hidden"
            >
              <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                <path d="M12 4 6 10l6 6" fill="none" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}

          <h2 className="min-w-0 flex-1 truncate text-body font-semibold">
            {selected ? selected.name : 'Send a template'}
          </h2>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="tap grid shrink-0 place-items-center rounded px-1 text-(--color-muted) hover:text-(--color-ink)"
          >
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="flex min-h-0 flex-1 sm:flex-row">
          {/* One pane at a time below sm, both above it — one tree, not two. */}
          <div className={`min-h-0 flex-1 sm:flex sm:flex-none ${selected ? 'hidden sm:block' : 'block'}`}>
            {list}
          </div>
          <div className={`min-h-0 flex-1 ${selected ? 'flex' : 'hidden sm:flex'}`}>
            {detail}
          </div>
        </div>
      </div>
    </div>
  );
}
